/**
 * Mobile sticky cart reminder (max-width 767px). Markup and styles: snippets/sticky-cart-reminder.liquid.
 *
 * - Visible only while the cart has items. The first count comes from Liquid; after that every
 *   cart change (theme events, or any /cart/add|change|update|clear request, including apps)
 *   triggers one /cart.js read, so the count is always the real cart.
 * - Placement follows the Zendesk Chat pill the same way the Feedback button does: same height,
 *   centred on it, mirrored to the left edge. With no Chat it uses the corner, above the PDP
 *   sticky buy bar when that is showing.
 * - The label is "View Cart" wherever it fits left of Feedback/Chat and "Cart" where it cannot
 *   (360px Android phones); only if even that has no room does it drop to the bag and count.
 *   It moves up above anything else pinned to that corner (cookie button, app teasers) and
 *   hides while a drawer, popup or chat window covers the screen.
 * - Tapping opens the mini cart exactly like the header bag. If the mini cart is out of date
 *   (cart changed without the theme re-rendering it), the theme's own cart:refresh opens it fresh.
 */
(() => {
  'use strict';

  if (window.__aetrexCartPillLoaded) return;
  window.__aetrexCartPillLoaded = true;

  const pill = document.getElementById('AetrexCartPill');
  if (!pill) return;

  const root = document.documentElement;
  const countEl = pill.querySelector('[data-cart-pill-count]');
  const labelEl = pill.querySelector('.aetrex-cart-pill__label');
  const LABELS = {
    full: (labelEl && labelEl.getAttribute('data-label-full')) || 'View Cart',
    short: (labelEl && labelEl.getAttribute('data-label-short')) || 'Cart'
  };
  const MOBILE = window.matchMedia('(max-width: 767px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ZENDESK = 'iframe#launcher, iframe[title="Button to launch messaging window"]';
  const FEEDBACK = '.aetrex-tf-cta';
  const GAP = 12;
  const FALLBACK = { height: 48, edge: 20, bottom: 12 };
  const FIRST_SHOW_WAIT_MS = 1200; // give Chat a moment to appear so the pill does not jump
  const TICK_MS = 200;
  const CART_MUTATION = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  const cartJsUrl = `${(pill.getAttribute('href') || '/cart').split(/[?#]/)[0].replace(/\/$/, '')}.js`;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const toCount = (value) => {
    const number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : 0;
  };

  let count = toCount(pill.getAttribute('data-cart-count'));
  let drawerCount = count; // the count the mini cart was last rendered with
  let bottom = FALLBACK.bottom;
  let visible = false;
  let everShown = false;
  let timer = 0;
  let measured = { key: '', full: 0, short: 0 };
  const startedAt = Date.now();

  // ---------------------------------------------------------------- visibility

  const setVisible = (next) => {
    if (next === visible) return;
    visible = next;
    if (next) everShown = true;
    pill.classList.toggle('is-visible', next);
  };

  const sync = () => {
    if (MOBILE.matches && count > 0) {
      if (!timer) timer = setInterval(tick, TICK_MS);
      tick();
      return;
    }
    if (timer) clearInterval(timer);
    timer = 0;
    setVisible(false);
  };

  // ---------------------------------------------------------------- count

  const bump = () => {
    if (!countEl || REDUCED_MOTION.matches) return;
    countEl.classList.remove('is-bump');
    void countEl.offsetWidth; // restart the animation
    countEl.classList.add('is-bump');
  };
  if (countEl) countEl.addEventListener('animationend', () => countEl.classList.remove('is-bump'));

  const setCount = (next) => {
    const value = Math.max(0, Math.floor(next));
    const grew = value > count;
    count = value;
    pill.setAttribute('data-cart-count', String(value));
    pill.setAttribute('aria-label', `View Cart, ${value} ${value === 1 ? 'item' : 'items'}`);
    const text = value > 99 ? '99+' : String(value);
    if (countEl && countEl.textContent !== text) {
      countEl.textContent = text;
      if (grew && visible) bump();
    }
    sync();
  };

  let pendingTimer = 0;
  let pendingFromTheme = false;
  let inflight = false;
  let rerun = false;
  let lastFetchAt = 0;

  // fromTheme: the theme has just re-rendered the mini cart, so it now matches the real cart.
  const schedule = (fromTheme) => {
    if (fromTheme) pendingFromTheme = true;
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(refresh, 150);
  };

  const refresh = async () => {
    if (inflight) {
      rerun = true;
      return;
    }
    inflight = true;
    const fromTheme = pendingFromTheme;
    pendingFromTheme = false;
    let read = false;
    try {
      const response = await fetch(cartJsUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (response.ok) {
        const cart = await response.json();
        if (cart && typeof cart.item_count === 'number') {
          read = true;
          if (fromTheme) drawerCount = cart.item_count;
          setCount(cart.item_count);
        }
      }
    } catch (error) {
      // Offline or blocked: keep the last known count.
    } finally {
      if (fromTheme && !read) pendingFromTheme = true;
      inflight = false;
      lastFetchAt = Date.now();
      if (rerun) {
        rerun = false;
        refresh();
      }
    }
  };

  // ---------------------------------------------------------------- placement

  // Zendesk's launcher iframe is larger than the Chat pill inside it, so measure the pill when the
  // frame allows it (same approach as the Feedback button), otherwise the frame itself.
  const launcherRect = (frame) => {
    const outer = frame.getBoundingClientRect();
    try {
      const doc = frame.contentDocument;
      const button = doc && (doc.querySelector('[data-testid="launcher"]') || doc.querySelector('button'));
      if (button) {
        const inner = button.getBoundingClientRect();
        if (inner.width > 10 && inner.height > 10) {
          return {
            left: outer.left + inner.left,
            right: outer.left + inner.right,
            top: outer.top + inner.top,
            bottom: outer.top + inner.bottom,
            width: inner.width,
            height: inner.height
          };
        }
      }
    } catch (error) {
      // Cross-origin frame: fall back to its box.
    }
    return { left: outer.left, right: outer.right, top: outer.top, bottom: outer.bottom, width: outer.width, height: outer.height };
  };

  const findZendesk = (vw, vh) => {
    const frames = document.querySelectorAll(ZENDESK);
    for (const frame of frames) {
      const style = getComputedStyle(frame);
      if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) continue;
      const rect = launcherRect(frame);
      if (rect.width < 10 || rect.height < 10) continue;
      if (rect.bottom <= 0 || rect.top >= vh || rect.right <= 0 || rect.left >= vw) continue;
      return rect;
    }
    return null;
  };

  const isNeighbour = (el) => !!(el.closest && el.closest(FEEDBACK)) || !!(el.matches && el.matches(ZENDESK));

  // The fixed (or bottom-sticky) box an element belongs to, if any. Page content that scrolls is
  // never an obstacle; Chat and Feedback are handled as neighbours instead.
  const pinnedBox = (el, memo) => {
    if (!el || el === root || el === document.body) return null;
    if (memo.has(el)) return memo.get(el);
    let box = null;
    if (!pill.contains(el) && !isNeighbour(el)) {
      const style = getComputedStyle(el);
      if (style.position === 'fixed' || (style.position === 'sticky' && style.bottom !== 'auto')) {
        box = parseFloat(style.opacity) < 0.05 ? null : el;
      } else {
        box = pinnedBox(el.parentElement, memo);
      }
    }
    memo.set(el, box);
    return box;
  };

  const findBlocker = (rect, vw, vh) => {
    if (typeof document.elementsFromPoint !== 'function') return null;
    const xs = [rect.left + 4, (rect.left + rect.right) / 2, rect.right - 4];
    const ys = [rect.top + 4, rect.bottom - 4];
    const memo = new Map();
    let top = Infinity;
    for (const x of xs) {
      for (const y of ys) {
        if (x < 0 || y < 0 || x >= vw || y >= vh) continue;
        for (const el of document.elementsFromPoint(x, y)) {
          const box = pinnedBox(el, memo);
          if (!box) continue;
          const r = box.getBoundingClientRect();
          if (r.width >= vw * 0.6 && r.height >= vh * 0.4) return { overlay: true };
          top = Math.min(top, r.top);
        }
      }
    }
    return top === Infinity ? null : { top };
  };

  const stickyBarBottom = () => {
    const bar = parseFloat(getComputedStyle(root).getPropertyValue('--sticky-buy-bar-height')) || 0;
    return bar > 0 ? bar + 16 : FALLBACK.bottom;
  };

  const setVar = (name, value) => {
    if (pill.style.getPropertyValue(name) !== value) pill.style.setProperty(name, value);
  };

  const setCompact = (compact) => {
    if (pill.classList.contains('is-compact') !== compact) pill.classList.toggle('is-compact', compact);
  };

  const setLabel = (mode) => {
    if (!labelEl) return;
    const text = mode === 'short' ? LABELS.short : LABELS.full;
    if (labelEl.textContent !== text) labelEl.textContent = text;
  };

  // Pill width with each label, re-measured only when something that changes it changes.
  const naturalWidths = (height) => {
    const fontsLoaded = document.fonts ? document.fonts.status : 'loaded';
    const key = `${height}|${countEl ? countEl.textContent : ''}|${fontsLoaded}`;
    if (measured.key !== key) {
      const wasCompact = pill.classList.contains('is-compact');
      const wasLabel = labelEl ? labelEl.textContent : '';
      if (wasCompact) pill.classList.remove('is-compact');
      setLabel('full');
      const full = pill.getBoundingClientRect().width;
      setLabel('short');
      const short = pill.getBoundingClientRect().width;
      if (labelEl) labelEl.textContent = wasLabel;
      if (wasCompact) pill.classList.add('is-compact');
      measured = { key, full, short };
    }
    return measured;
  };

  const place = (zendesk, vw, vh) => {
    const feedbackBox = document.querySelector(`${FEEDBACK}.is-visible`);
    const feedbackButton = feedbackBox ? feedbackBox.querySelector('.aetrex-tf-cta__open') : null;
    const feedback = feedbackButton ? feedbackButton.getBoundingClientRect() : null;
    const ref = zendesk || (feedback && feedback.width > 10 ? feedback : null);

    const height = ref ? clamp(Math.round(ref.height), 32, 64) : FALLBACK.height;
    const edge = ref ? clamp(Math.round(vw - ref.right), 8, 24) : FALLBACK.edge;

    setVar('--acp-h', `${height}px`);
    if (pill.style.left !== `${edge}px`) pill.style.left = `${edge}px`;

    const widths = naturalWidths(height);
    const base = pill.getBoundingClientRect(); // laid out at the current `bottom`

    // Row: centred on Chat (or Feedback); otherwise the corner, above the sticky buy bar.
    let target = ref ? bottom + (base.top + base.height / 2) - (ref.top + ref.height / 2) : stickyBarBottom();

    const neighbours = [feedbackBox ? feedbackBox.getBoundingClientRect() : null, zendesk].filter(Boolean);

    for (let pass = 0; pass < 4; pass += 1) {
      const shift = target - bottom;
      const top = base.top - shift;
      const rowBottom = base.bottom - shift;

      let limit = vw;
      neighbours.forEach((n) => {
        if (n.width > 0 && n.left > base.left && n.top < rowBottom && n.bottom > top) limit = Math.min(limit, n.left);
      });
      const room = limit - GAP - base.left;
      const mode = widths.full <= room ? 'full' : widths.short <= room ? 'short' : 'compact';
      const width = mode === 'full' ? widths.full : mode === 'short' ? widths.short : height;
      if (mode === 'compact' && height > room) return false;

      const rect = { left: base.left, right: base.left + width, top, bottom: rowBottom };
      const blocker = findBlocker(rect, vw, vh);
      if (blocker && blocker.overlay) return false;
      if (!blocker) {
        setLabel(mode);
        setCompact(mode === 'compact');
        if (Math.abs(target - bottom) >= 0.25 || !pill.style.bottom) {
          bottom = target;
          pill.style.bottom = `${Math.round(target * 100) / 100}px`;
        }
        return top > vh * 0.35;
      }
      target += rowBottom - (blocker.top - GAP); // sit just above whatever holds the corner
    }
    return false;
  };

  function tick() {
    if (!MOBILE.matches || count <= 0) {
      sync();
      return;
    }
    if (document.hidden) return;
    if (pill.hidden) {
      // Give the browser one frame at opacity 0 before showing, or the fade-in is skipped.
      pill.hidden = false;
      return;
    }

    let show = false;
    try {
      const vw = root.clientWidth;
      const vh = window.innerHeight;
      const zendesk = findZendesk(vw, vh);
      if (!zendesk && !everShown && Date.now() - startedAt < FIRST_SHOW_WAIT_MS) return;
      show = place(zendesk, vw, vh);
    } catch (error) {
      show = false;
    }
    setVisible(show);
  }

  // ---------------------------------------------------------------- open the mini cart

  pill.addEventListener('click', (event) => {
    if (!MOBILE.matches) return;
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return; // no mini cart on this page: the link opens the cart page

    if (count !== drawerCount) {
      // The cart changed without the theme re-rendering the mini cart (another tab, an app,
      // back/forward cache). Let the theme fetch it fresh and open it.
      const sectionId = drawer.getAttribute('data-section-id');
      if (!sectionId || !document.getElementById(`MiniCart-${sectionId}`)) return; // cart page instead
      event.preventDefault();
      drawerCount = count;
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      return;
    }

    const headerBag = Array.from(document.querySelectorAll('a.cart-drawer-button')).find(
      (el) => el.getClientRects().length > 0
    );
    if (headerBag) {
      event.preventDefault();
      headerBag.click();
      return;
    }
    if (typeof drawer.show === 'function') {
      event.preventDefault();
      drawer.show();
    }
  });

  // ---------------------------------------------------------------- listen for cart changes

  const pubsub = window.theme && window.theme.pubsub;
  if (pubsub && typeof pubsub.subscribe === 'function' && pubsub.PUB_SUB_EVENTS) {
    if (pubsub.PUB_SUB_EVENTS.cartUpdate) pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, () => schedule(true));
    if (pubsub.PUB_SUB_EVENTS.cartError) pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartError, () => schedule(false));
  }
  ['cart:updated', 'cart:refresh', 'ajaxProduct:added'].forEach((name) => {
    document.addEventListener(name, () => schedule(true));
  });

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => CART_MUTATION.test(entry.name))) schedule(false);
      }).observe({ type: 'resource' });
    } catch (error) {
      // Resource timing unavailable: theme events still keep the count current.
    }
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule(false); // restored from the back/forward cache
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && Date.now() - lastFetchAt > 5000) schedule(false); // cart may have changed in another tab
  });
  if (MOBILE.addEventListener) MOBILE.addEventListener('change', sync);
  else if (MOBILE.addListener) MOBILE.addListener(sync);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      measured.key = '';
    });
  }

  sync();
})();
